/**
 * Benchmark Utilities for Parent Portal
 *
 * Contextualizes mastery scores against group-level norms.
 * Consolidated from GrowthPage and VoortgangPage (D-SCAN D-3).
 */

export interface BenchmarkResult {
  label: string
  emoji: string
  colorClass: string
  bgClass: string
  description: string
}

/**
 * Get benchmark context for a mastery score relative to group norms.
 * Thresholds are calibrated per group level (Dutch primary education norms).
 * Higher groups have slightly lower "on track" thresholds because content is harder.
 *
 * @param mastery - Mastery percentage (0-100)
 * @param group - School group (6, 7, or 8)
 * @returns Benchmark result with label, emoji, colors, and description
 */
export function getBenchmarkContext(
  mastery: number,
  group: number,
): BenchmarkResult {
  const thresholds = {
    6: { excellent: 80, good: 60, average: 40 },
    7: { excellent: 75, good: 55, average: 35 },
    8: { excellent: 70, good: 50, average: 30 },
  }
  const t = thresholds[group as 6 | 7 | 8] || thresholds[7]

  if (mastery >= t.excellent) {
    return {
      label: 'Uitstekend',
      emoji: '🌟',
      colorClass: 'text-emerald-700',
      bgClass: 'bg-emerald-50 border-emerald-200',
      description: `Dit is uitstekend voor groep ${group}!`,
    }
  }
  if (mastery >= t.good) {
    return {
      label: 'Goed op schema',
      emoji: '👍',
      colorClass: 'text-emerald-600',
      bgClass: 'bg-emerald-50/50 border-emerald-100',
      description: `Goed op schema voor groep ${group}.`,
    }
  }
  if (mastery >= t.average) {
    return {
      label: 'Gemiddeld',
      emoji: '📊',
      colorClass: 'text-amber-600',
      bgClass: 'bg-amber-50 border-amber-200',
      description: `Gemiddeld voor groep ${group}. Extra oefening helpt!`,
    }
  }
  return {
    label: 'Extra oefening nodig',
    emoji: '💪',
    colorClass: 'text-rose-600',
    bgClass: 'bg-rose-50 border-rose-200',
    description: `Onder gemiddeld voor groep ${group}. Regelmatig oefenen maakt het verschil.`,
  }
}
